import React from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { ArrowRight, BarChart3, CalendarDays, CalendarX, ExternalLink, LayoutGrid, MessageCircle, Play, User, UserCog, Users } from "lucide-react";
import { getDashboardMenu, getRole, ROLE_LABELS, ROLES } from "../utils/roles";
import { visitSitePath } from "../utils/appSlug";
import authService from "../services/auth.service";
import { PLATFORM_APPLICATION, useApplication } from "../context/ApplicationContext";
import { resolveAssetUrl } from "../utils/assets";
import "./dashboard.css";

const MENU_ICONS = {
  applicazioni: LayoutGrid,
  gestori: UserCog,
  eventi: CalendarDays,
  "inizia-eventi": Play,
  "termina-eventi": CalendarX,
  sondaggi: BarChart3,
  "chat-evento": MessageCircle,
  iscrizioni: Users,
  utenti: Users,
  profilo: User,
};

const DashboardLayout = ({ loginStatus, setLoginStatus, children }) => {
  const location = useLocation();
  const history = useHistory();
  const { application } = useApplication();
  const menu = getDashboardMenu(loginStatus);
  const role = getRole(loginStatus);
  const isSuperadmin = role === ROLES.SUPERADMIN;
  const isPlatformUser = role === ROLES.ADMIN || role === ROLES.SUPERADMIN;
  const brandName = isPlatformUser
    ? "WebinarPlus"
    : (application?.site_title || application?.name || "Applicazione");
  const logoSrc = isPlatformUser
    ? resolveAssetUrl(PLATFORM_APPLICATION.logo_url)
    : (application?.logo_url ? resolveAssetUrl(application.logo_url) : "");
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const sitePath = visitSitePath(loginStatus);
  const currentItem = menu.find((item) => isActive(item.path));
  const displayName = (loginStatus?.fullname || "").trim() || loginStatus?.sub || "Utente";
  const initials = displayName
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "U";

  const logout = () => {
    authService.logout();
    if (setLoginStatus) setLoginStatus({});
    history.push(sitePath);
  };

  return (
    <div className={`dashboard-shell dashboard-shell--${role}${isSuperadmin ? " dashboard-shell--superadmin" : ""}`}>
      <aside className="dashboard-sidebar">
        <Link to="/dashboard" className="dashboard-sidebar__brand" aria-label={`Dashboard ${brandName}`}>
          {logoSrc && (
            <img
              src={logoSrc}
              alt=""
              className={`dashboard-sidebar__logo${isPlatformUser ? " dashboard-sidebar__logo--platform" : " dashboard-sidebar__logo--app"}`}
            />
          )}
          {!isPlatformUser && (
            <span className="dashboard-sidebar__brand-copy">
              <strong>{brandName}</strong>
              <small>{application?.site_subtitle || "Area gestore"}</small>
            </span>
          )}
        </Link>
        <p className="dashboard-sidebar__label">Workspace</p>
        <nav className="dashboard-nav" aria-label="Menu area riservata">
          {menu.map((item) => {
            const Icon = MENU_ICONS[item.key];
            return (
              <Link
                key={item.key}
                to={item.path}
                className={`dashboard-nav__link${
                  isActive(item.path) ? " dashboard-nav__link--active" : ""
                }`}
              >
                {Icon && <Icon size={18} strokeWidth={1.75} aria-hidden="true" />}
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="dashboard-sidebar__footer">
          <div className="dashboard-sidebar__profile">
            <span className="dashboard-avatar" aria-hidden="true">{initials}</span>
            <span className="dashboard-sidebar__profile-copy">
              <strong>{displayName}</strong>
              <small>{ROLE_LABELS[role] || role}</small>
            </span>
          </div>
          <div className="dashboard-sidebar__actions">
            <Link to={sitePath} className="dashboard-sidebar__action">
              <ExternalLink size={18} strokeWidth={1.75} aria-hidden="true" />
              <span>Vai al sito</span>
            </Link>
            <button type="button" className="dashboard-sidebar__action" onClick={logout}>
              <ArrowRight size={18} strokeWidth={1.75} aria-hidden="true" />
              <span>Esci</span>
            </button>
          </div>
        </div>
      </aside>
      <main className="dashboard-workspace">
        <header className="dashboard-topbar">
          <div>
            <p className="dashboard-topbar__eyebrow">
              {ROLE_LABELS[role] || role} · {currentItem?.label || "Dashboard"}
            </p>
            <h1>Ciao, {displayName.split(" ")[0]}</h1>
            <p>Gestisci attività, contenuti e partecipanti da un unico spazio.</p>
          </div>
          <div className="dashboard-topbar__identity">
            <span className="dashboard-avatar dashboard-avatar--light" aria-hidden="true">{initials}</span>
            <span>
              <strong>{displayName}</strong>
              <small>{ROLE_LABELS[role] || role}</small>
            </span>
          </div>
        </header>
        <div className="dashboard-content">{children}</div>
      </main>
    </div>
  );
};

export default DashboardLayout;
